import { flatten, uniqBy } from 'lodash'
import moment from 'moment'
import {
    ForwardedRef,
    Fragment,
    RefObject,
    forwardRef,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react'
import { useSearchParams } from 'react-router-dom'

import { Block } from '../../models/Block'
import {
    Bookable,
    BookablesByDate,
    getBookableColor,
    getBookableMultiDayLabel,
    getBookableStatus,
    getBookableTimeLabel,
    isFixedEventReservationItem,
    isMultiDay,
} from '../../models/Bookable'
import { Booking } from '../../models/Booking'
import { ComputeFactory } from '../../models/ComputeFactory'
import { Item } from '../../models/Item'
import { Lesson } from '../../models/Lesson'
import { PSEvent } from '../../models/PSEvent'
import { Payee } from '../../models/Payee'
import { User } from '../../models/User'
import {
    DATE_SHORT_ISO_FORMAT,
    SHORT_DOW_MONTH_DAY_FORMAT,
    isPast,
    isToday,
} from '../../utils/dates'
import { Icon } from '../Icon'
import { Spinner } from '../Spinner'
import { PSApplication } from '../app/PSApplication'
import Label from '../app/design/Label'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import Tag from '../app/design/Tag'
import { ClientTableCell } from '../dashboards/ClientTableCell'
import { ProListTableCell } from '../dashboards/ProListTableCell'
import { ServiceListTableCell } from '../dashboards/ServiceListTableCell'
import { CalendarBookableTableRow } from './CalendarBookableTableRow'
import { ClientListTableCell } from './ClientListTableCell'

type Props = {
    bookablesByDate: BookablesByDate
    pros: User[]
    emptyState: JSX.Element
}

type TableRefs = {
    headerRef: RefObject<HTMLTableSectionElement>
    todayRowRef: RefObject<HTMLTableRowElement>
}

function LessonRosterListCell({ lesson }: { lesson: Lesson }) {
    const [roster, setRoster] = useState<User[]>([])
    const [isFetching, setIsFetching] = useState(false)

    useEffect(() => {
        ;(async () => {
            setIsFetching(true)
            const bookings = await ComputeFactory.getInstance().fetchClassBookings(lesson)
            const roster = bookings.filter((b) => !!b.client).map((b) => b.client) as User[]
            setIsFetching(false)
            setRoster(roster)
        })()
    }, [lesson])

    if (isFetching) {
        return <Spinner size={24} />
    }

    return roster.length ? (
        <ClientListTableCell clients={roster} size="xs" />
    ) : (
        <div className="flex items-center gap-4">
            <Icon icon="users.small" background="round" className="size-6" />
            <Paragraph size="sm">0 clients</Paragraph>
        </div>
    )
}

function SpotsLeftStringCell({ record }: { record: Lesson | Item }) {
    const [spotsLeft, setSpotsLeft] = useState<string>('')

    useEffect(() => {
        ;(async () => {
            const booked =
                record instanceof Lesson ? await record.numberOfBookings() : await record.reserved()
            const left = !!record.capacity ? record.capacity - booked : 0
            setSpotsLeft(left <= 0 ? 'Full' : left.toString())
        })()
    }, [record])

    return (
        <Paragraph size="sm" className="text-gray-700">
            {!!spotsLeft ? spotsLeft : <Spinner size={24} />}
        </Paragraph>
    )
}

export const CalendarBookableTable = forwardRef(
    ({ bookablesByDate, pros, emptyState }: Props, ref: ForwardedRef<TableRefs>) => {
        const [searchParams] = useSearchParams()
        const payees = useMemo(() => pros.map((u) => u.subtype) as Payee[], [pros])

        const headerRef = useRef<HTMLTableSectionElement>(null)
        const todayRowRef = useRef<HTMLTableRowElement>(null)

        useImperativeHandle(ref, () => ({
            headerRef,
            todayRowRef,
        }))

        const generateHref = (date: string) => {
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.set('view', 'day')
            newSearchParams.set('date', moment(date).format(DATE_SHORT_ISO_FORMAT))
            return `/calendar?${newSearchParams.toString()}`
        }

        const showSpotsLeft = useMemo(() => {
            const owner = PSApplication.owner()
            return owner.allowClass || owner.allowReservation
                ? !!flatten(Object.values(bookablesByDate)).filter(
                      (bookable) =>
                          bookable instanceof Lesson || isFixedEventReservationItem(bookable)
                  ).length
                : false
        }, [bookablesByDate])

        const isPrivateEvent = (bookable: Bookable) =>
            bookable instanceof PSEvent && !bookable.ownerIsMe

        return (
            <Table>
                <TableHeader ref={headerRef}>
                    <TableRow location="head">
                        <TableHead></TableHead>
                        <TableHead className="min-h-12 py-3 pl-3 font-normal">Time</TableHead>
                        <TableHead className="min-h-12 py-3 font-normal">Client</TableHead>
                        <TableHead className="min-h-12 py-3 font-normal">Event</TableHead>
                        {/* <TableHead className="font-normal min-h-12 py-3">Resource</TableHead> */}
                        <TableHead className="min-h-12 py-3 font-normal">Pro</TableHead>
                        {showSpotsLeft && (
                            <TableHead className="min-h-12 py-3 font-normal">Spots left</TableHead>
                        )}
                        <TableHead className="min-h-12 py-3 font-normal">Status</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {Object.keys(bookablesByDate).length > 0 &&
                        Object.entries(bookablesByDate).map(([date, bookables]) => (
                            <Fragment key={date}>
                                <ClickableTableRow
                                    className="cursor-pointer bg-gray-100 hover:bg-gray-100 hover:bg-linear-to-b hover:from-black/4 hover:to-black/4"
                                    ref={isToday(date) ? todayRowRef : null}
                                    href={generateHref(date)}
                                    target="_blank"
                                >
                                    <TableCell></TableCell>
                                    <TableCell className="p-3" colSpan={showSpotsLeft ? 6 : 5}>
                                        <Label
                                            size="sm"
                                            className={`pointer-events-none ${isToday(date) ? 'text-info-400' : 'text-gray-600'}`}
                                        >
                                            {moment(date).format(SHORT_DOW_MONTH_DAY_FORMAT)}
                                        </Label>
                                    </TableCell>
                                </ClickableTableRow>
                                {bookables.map((bookable) => (
                                    <CalendarBookableTableRow
                                        key={bookable.id}
                                        className={`${isPast(bookable.endDate) ? 'opacity-60' : 'opacity-100'}`}
                                        bookable={bookable}
                                        isClickable={!isPrivateEvent(bookable)}
                                    >
                                        <TableCell className="w-12 pr-0">
                                            <div
                                                className="h-3 w-3 rounded-full bg-gray-400"
                                                style={{
                                                    backgroundColor: getBookableColor(
                                                        bookable,
                                                        payees
                                                    ).fg,
                                                }}
                                            ></div>
                                        </TableCell>
                                        <TableCell className="pl-3">
                                            <div className="flex items-center gap-3">
                                                <div>
                                                    <Label
                                                        size="sm"
                                                        className="pointer-events-none flex gap-3 text-gray-700"
                                                    >
                                                        {getBookableTimeLabel(
                                                            bookable,
                                                            moment(date),
                                                            true
                                                        )}
                                                    </Label>
                                                    {isMultiDay(bookable) && (
                                                        <Label
                                                            size="sm"
                                                            className="pointer-events-none"
                                                        >
                                                            {getBookableMultiDayLabel(
                                                                bookable,
                                                                moment(date)
                                                            )}
                                                        </Label>
                                                    )}
                                                </div>
                                                {bookable.isRecurring && (
                                                    <Icon
                                                        icon="repeat.small"
                                                        className="shrink-0"
                                                        shouldInheritColor
                                                    />
                                                )}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {bookable instanceof PSEvent ||
                                            bookable instanceof Block ? (
                                                <Paragraph size="sm">-</Paragraph>
                                            ) : bookable instanceof Lesson ? (
                                                <LessonRosterListCell lesson={bookable} />
                                            ) : (
                                                <ClientTableCell
                                                    user={bookable.person}
                                                    subaccount={bookable.subaccount}
                                                    size="xs"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {bookable instanceof Booking ? (
                                                <ServiceListTableCell services={bookable.lines} />
                                            ) : bookable instanceof Lesson && bookable.item ? (
                                                <ServiceListTableCell services={[bookable.item]} />
                                            ) : (
                                                <div className="flex items-center gap-4">
                                                    {(bookable instanceof Block ||
                                                        bookable instanceof PSEvent) && (
                                                        <Icon
                                                            className="size-6"
                                                            icon={`${bookable instanceof Block ? 'clockLock.small' : 'calendarRefresh.small'}`}
                                                            background="round"
                                                        />
                                                    )}
                                                    <Paragraph size="sm">
                                                        {isPrivateEvent(bookable)
                                                            ? 'Private event'
                                                            : bookable.title || '-'}
                                                    </Paragraph>
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {bookable instanceof Booking ? (
                                                <ProListTableCell
                                                    pros={uniqBy(
                                                        bookable.jobs
                                                            .map((j) => j.person)
                                                            .filter((p) => !!p) as User[],
                                                        'id'
                                                    )}
                                                    size="xs"
                                                />
                                            ) : bookable instanceof PSEvent && !!bookable.owner ? (
                                                <ProListTableCell
                                                    pros={[bookable.owner]}
                                                    size="xs"
                                                />
                                            ) : !!bookable.person ? (
                                                <ProListTableCell
                                                    pros={[bookable.person]}
                                                    size="xs"
                                                />
                                            ) : (
                                                <Paragraph size="sm">-</Paragraph>
                                            )}
                                        </TableCell>
                                        {showSpotsLeft && (
                                            <TableCell>
                                                {bookable instanceof Lesson ? (
                                                    <SpotsLeftStringCell record={bookable} />
                                                ) : isFixedEventReservationItem(bookable) ? (
                                                    <SpotsLeftStringCell
                                                        record={bookable as Item}
                                                    />
                                                ) : (
                                                    <Paragraph size="sm">-</Paragraph>
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell>
                                            {!!getBookableStatus(bookable).status ? (
                                                <Tag
                                                    className="pointer-events-none w-fit capitalize"
                                                    size="sm"
                                                    label={getBookableStatus(bookable).status}
                                                    style={{
                                                        backgroundColor:
                                                            getBookableStatus(bookable).color.bg,
                                                        color: getBookableStatus(bookable).color.fg,
                                                    }}
                                                />
                                            ) : (
                                                <Paragraph size="sm">-</Paragraph>
                                            )}
                                        </TableCell>
                                    </CalendarBookableTableRow>
                                ))}
                            </Fragment>
                        ))}
                    {Object.keys(bookablesByDate).length === 0 && (
                        <TableRow>
                            <TableCell className="py-9" colSpan={showSpotsLeft ? 7 : 6}>
                                {emptyState}
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        )
    }
)
